import { t } from "i18next"
import { Card, ComponentState, Group, RadioButtons, Skeleton, Spinner, Title } from "components/common"
import { DATE_FORMAT, DATE_MONTH_FORMAT } from "constants"
import { useCallback, useMemo, useState } from "react"
import { convertToHMSString, numberConvertor, numberFormat } from "utils"

import { BarChart, LineChart } from "components/charts"
import theme from "utils/themeVars"
import { ListsDropdown } from ".."
import { dateTimeFunction } from "utils/date"
import Flex from "components/common/flex"

const AnalyticChart = ({
  className,
  dataComparison,
  staffRegionComparison,
  listKeys,
  defaultComparison,
  comparisonLoading,
  kpiLoading,
  refetchComparison,
  refetchKpi,
  comparisonError,
  kpiError,
  from,
  ...props
}) => {
  const [values, setValues] = useState({})
  const [selectedChart, setSelectedChart] = useState("bar-chart")
  const charOptions = useMemo(() => {
    return [
      { label: t("Line Chart"), value: "line-chart" },
      { label: t("Bar Chart"), value: "bar-chart" },
    ]
  }, [])

  const timeComparisonData = useMemo(() => {
    const firstList = [],
      secondList = []
    for (const key in dataComparison?.data) {
      values?.first &&
        firstList.push({
          name: values?.first?.label,
          date: dateTimeFunction({ date: key, format: DATE_FORMAT, apiRequest: true }),
          number: Number(dataComparison?.data[key]?.[values?.first?.parent]?.[values?.first?.key]),
          type: dataComparison?.staticKeys?.[values?.first?.parent]?.[values?.first?.key]?.type,
        })
      values?.second &&
        secondList.push({
          name: values?.second?.label,
          date: dateTimeFunction({ date: key, format: DATE_FORMAT, apiRequest: true }),
          number: Number(dataComparison?.data[key]?.[values?.second?.parent]?.[values?.second?.key]),
          type: dataComparison?.staticKeys?.[values?.second?.parent]?.[values?.second?.key]?.type,
        })
    }
    return firstList.concat(secondList)
  }, [values, dataComparison])

  const regionStaffData = useMemo(() => {
    const firstList = [],
      secondList = []
    for (const key in staffRegionComparison?.data) {
      values?.first &&
        firstList.push({
          name: values?.first?.label,
          title: staffRegionComparison?.data[key]?.name,
          number: Number(staffRegionComparison?.data[key]?.[values?.first?.parent]?.[values?.first?.key]),
          type: dataComparison?.staticKeys?.[values?.first?.parent]?.[values?.first?.key]?.type,
        })
      values?.second &&
        secondList.push({
          name: values?.second?.label,
          title: staffRegionComparison?.data[key]?.name,
          number: Number(staffRegionComparison?.data[key]?.[values?.second?.parent]?.[values?.second?.key]),
          type: dataComparison?.staticKeys?.[values?.second?.parent]?.[values?.second?.key]?.type,
        })
    }
    return firstList.concat(secondList)
  }, [values, staffRegionComparison])
  const getConversionRatio = items => {
    if (items[1]?.data?.number == 0) return 0
    else {
      let firstMetric = items[0]?.data?.number
      let secondMetric = items[1]?.data?.number
      let ratio = firstMetric / secondMetric
      return numberFormat(ratio.toFixed(2))
    }
  }
  const addConversionRatio = items => {
    const updatedItems = items?.map(item => ({
      ...item,
      value: item?.data?.type == "time" ? convertToHMSString(item?.data?.number) : item?.data?.number,
    }))

    if (items?.length > 1) {
      let obj = {
        ...items[0],
        name: `${t("Ratio")} (${items[0]?.name + " per " + items[1]?.name})`,
        value: getConversionRatio(items),
        color: "#A7A7A7",
      }
      return [...updatedItems, obj]
    }
    return updatedItems
  }

  const renderStaffRegionChart = useCallback(
    Component => {
      return (
        <Component
          data={regionStaffData}
          xField="title"
          yField="number"
          seriesField="name"
          yAxis={{ label: { formatter: v => numberConvertor(v, "intl", 2) } }}
          colorField="name"
          color={({ name }) => (name === values?.first?.label ? theme["error-color"] : theme["link-hover"])}
          tooltip={{
            customItems: originalItems => {
              return addConversionRatio(originalItems)
            },
            formatter: datum => {
              const convertedValue = numberFormat(datum?.number, "en-US", 2)
              return { name: datum?.name, value: convertedValue }
            },
          }}
          disabled={kpiLoading}
          isGroup={selectedChart == "bar-chart"}
          legend={{ position: "bottom" }}
          loading={kpiLoading}
        />
      )
    },
    [selectedChart, regionStaffData, kpiLoading]
  )

  return (
    <Card
      className={className}
      title={<Title level={3}>{t("Analytics")}</Title>}
      extra={
        <ListsDropdown
          loading={comparisonLoading || kpiLoading}
          disabled={comparisonLoading || kpiLoading}
          list={listKeys}
          onChange={obj => setValues(obj)}
          defaults={defaultComparison}
          from={from}
        />
      }
      headStyle={{ borderBottom: 0 }}
    >
      <Group template="repeat(2, 1fr)" gap="64px">
        {comparisonError ? (
          <ComponentState
            description={comparisonError?.message}
            status={comparisonError?.status}
            btnLoading={comparisonLoading}
            onClick={() => refetchComparison()}
          />
        ) : (
          <div className="pos-rel">
            <div style={{ minHeight: 32 }}>
              {comparisonLoading ? <Skeleton /> : <Title level={5}>{t("Over Time Comparison")}</Title>}
            </div>

            <LineChart
              data={timeComparisonData}
              xField="date"
              yField="number"
              seriesField="name"
              xAxis={{
                label: {
                  formatter: v => dateTimeFunction({ date: v, format: DATE_MONTH_FORMAT }),
                },
              }}
              yAxis={{
                label: { formatter: v => numberConvertor(v, "intl", 2) },
              }}
              colorField="name"
              color={({ name }) => (name === values?.first.label ? theme["error-color"] : theme["link-hover"])}
              tooltip={{
                customItems: originalItems => addConversionRatio(originalItems),
                formatter: datum => ({
                  name: datum.name,
                  value: numberFormat(datum.number, "en-US", 2),
                }),
              }}
              disabled={comparisonLoading}
              legend={{ position: "bottom" }}
              loading={comparisonLoading}
            />
          </div>
        )}

        {kpiError ? (
          <ComponentState
            description={kpiError?.message}
            status={kpiError?.status}
            btnLoading={kpiLoading}
            onClick={() => refetchKpi()}
          />
        ) : (
          <div className="pos-rel">
            <Flex justify="space-between">
              {kpiLoading ? <Skeleton /> : <Title level={5}>{t("Staff/Region Comparison")}</Title>}
              {!kpiLoading && (
                <RadioButtons
                  options={charOptions}
                  getValue={e => e?.value}
                  onChange={selected => {
                    setSelectedChart(selected?.target?.value)
                  }}
                  value={selectedChart}
                />
              )}
            </Flex>

            {renderStaffRegionChart(selectedChart === "line-chart" ? LineChart : BarChart)}
          </div>
        )}
      </Group>
    </Card>
  )
}
export default AnalyticChart
